import { Text } from "@dropbox/dig-components/typography";
import { useDashUserAttributes } from "@mirage/shared/eligibility/hooks/useDashUserAttributes";
import i18n, { Trans } from "@mirage/translations";
import { useCallback, useEffect, useState } from "react";
import { ActionButtonType } from "../self-serve-onboarding/SelfServeOnboarding";
import styles from "../self-serve-onboarding/SelfServeOnboarding.module.css";
import { SelfServeOnboardingStep } from "../self-serve-onboarding/types";

import useAvailableExtensions from "@mirage/settings/utils/useAvailableExtensions";
import { browserNameForCurrentBrowser } from "@mirage/shared/util/webExtensionsHelpers";
import type { OnboardingStepItem } from "../self-serve-onboarding/SelfServeOnboarding";

const EXTENSION_UPSELL_STEP = {
  step: SelfServeOnboardingStep.DOWNLOAD_EXTENSION_UPSELL,
  title: i18n.t("team_onboarding_extension_upsell_title"),
  description: i18n.t("team_onboarding_extension_upsell_description", {
    browserName: browserNameForCurrentBrowser(),
  }),
  primaryActions: [ActionButtonType.Later],
  skipValidation: true,
};

const DESKTOP_UPSELL_STEP = {
  step: SelfServeOnboardingStep.DOWNLOAD_UPSELL,
  title: i18n.t("self_serve_download_upsell_title"),
  description: i18n.t("self_serve_download_upsell_description"),
  primaryActions: [ActionButtonType.Later, ActionButtonType.Download],
  skipValidation: true,
};

const ADMIN_ONBOARDING_STEPS: OnboardingStepItem[] = [
  {
    step: SelfServeOnboardingStep.COMPANY_INFO,
    title: i18n.t("self_serve_company_info_title"),
    description: i18n.t("self_serve_company_info_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.PROFILING,
    title: i18n.t("self_serve_profiling_title"),
    description: i18n.t("self_serve_profiling_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.JTBD,
    title: i18n.t("self_serve_jtbd_title"),
    description: i18n.t("self_serve_jtbd_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.CONNECTORS,
    title: i18n.t("self_serve_connectors_title"),
    description: "",
    primaryActions: [ActionButtonType.Continue],
    skipValidation: true,
  },
  {
    step: SelfServeOnboardingStep.TEAM_INVITE,
    title: i18n.t("self_serve_team_invite_title"),
    primaryActions: [ActionButtonType.Skip, ActionButtonType.Continue],
  },
  // Creating a separate step for large team onboarding and avoid showing it in the main flow
  {
    step: SelfServeOnboardingStep.LARGE_TEAM,
    title: "", // This step is hidden, so it does not need a title or description
    description: "",
    primaryActions: [],
    skipValidation: true,
    hidden: true, // This step is hidden because it is outside of the main flow of onboarding
  },
  {
    step: SelfServeOnboardingStep.ERROR,
    title: "", // This step is hidden, so it does not need a title or description
    description: "",
    primaryActions: [],
    skipValidation: true,
    hidden: true, // This step is hidden because it is outside of the main flow of onboarding
  },
];

const TEAM_MEMBER_ONBOARDING_STEPS: OnboardingStepItem[] = [
  {
    step: SelfServeOnboardingStep.PROFILING,
    title: i18n.t("self_serve_profiling_title"),
    description: i18n.t("self_serve_profiling_description"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.CONNECTORS,
    title: i18n.t("self_serve_connectors_title"),
    primaryActions: [ActionButtonType.Continue],
  },
  {
    step: SelfServeOnboardingStep.TEAM_INVITE,
    title: i18n.t("self_serve_team_invite_title"),
    primaryActions: [ActionButtonType.Skip, ActionButtonType.Continue],
  },
];

export const useSelfServeOnboardingSteps = (
  isAdmin: boolean,
  canSkipCompanyInfoStep: boolean = false,
  canSkipProfilingStep: boolean = false,
) => {
  const [onboardingSteps, setOnboardingSteps] = useState<
    OnboardingStepItem[] | undefined
  >(undefined);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const { extensionAvailableForBrowser } = useAvailableExtensions();
  const { userAttributes, refetch: refetchDashUserAttributes } =
    useDashUserAttributes();
  const [canSeeDfbInviteMemberForm, setCanSeeDfbInviteMemberForm] = useState(
    !!userAttributes?.canSeeDfbInviteMemberForm,
  );

  useEffect(() => {
    setCanSeeDfbInviteMemberForm(!!userAttributes?.canSeeDfbInviteMemberForm);
  }, [userAttributes]);

  useEffect(() => {
    const steps = getLatestOnboardingSteps(
      isAdmin,
      canSeeDfbInviteMemberForm,
      extensionAvailableForBrowser,
      canSkipCompanyInfoStep,
      canSkipProfilingStep,
    );
    setOnboardingSteps(steps);
  }, [
    isAdmin,
    canSeeDfbInviteMemberForm,
    extensionAvailableForBrowser,
    canSkipCompanyInfoStep,
    canSkipProfilingStep,
  ]);

  const getLatestOnboardingSteps = (
    isAdmin: boolean,
    canSeeDfbInviteMemberForm: boolean,
    isExtensionAvailable: boolean,
    canSkipCompanyInfoStep: boolean,
    canSkipProfilingStep: boolean,
  ) => {
    const steps = [
      ...(isAdmin ? ADMIN_ONBOARDING_STEPS : TEAM_MEMBER_ONBOARDING_STEPS),
    ];

    steps.push(
      isExtensionAvailable ? EXTENSION_UPSELL_STEP : DESKTOP_UPSELL_STEP,
    );

    return steps
      .filter(
        (step) =>
          (canSeeDfbInviteMemberForm ||
            step.step !== SelfServeOnboardingStep.TEAM_INVITE) &&
          (!canSkipCompanyInfoStep ||
            step.step !== SelfServeOnboardingStep.COMPANY_INFO) &&
          (!canSkipProfilingStep ||
            step.step !== SelfServeOnboardingStep.PROFILING),
      )
      .map((step) =>
        step.step === SelfServeOnboardingStep.CONNECTORS
          ? {
              ...step,
              description: (
                <Trans
                  i18nKey={"self_serve_connectors_description"}
                  components={{
                    policyLink: (
                      <Text
                        className={styles.privacyLink}
                        onClick={() => setShowPrivacyModal(true)}
                      />
                    ),
                  }}
                />
              ),
            }
          : step,
      );
  };

  const refreshSteps = useCallback(async () => {
    await refetchDashUserAttributes();
  }, [refetchDashUserAttributes]);

  return {
    onboardingSteps,
    showPrivacyModal,
    setShowPrivacyModal,
    refreshSteps,
  };
};
